import type { ContentRecord } from "@/lib/content-types";
import { externalResultsUrl } from './athlete-profiles';

function text(data: Record<string, unknown>, key: string): string {
  return typeof data[key] === "string" ? data[key] as string : "";
}

function list(data: Record<string, unknown>, key: string): string[] {
  return Array.isArray(data[key]) ? (data[key] as unknown[]).map(String).filter(Boolean) : [];
}

export function toResult(record: ContentRecord) {
  return {
    id: record.id,
    title: record.title,
    date: text(record.data, "date"),
    place: text(record.data, "place"),
    lead: text(record.data, "lead"),
    results: list(record.data, "results"),
    featured: record.data.featured === true,
  };
}

export function toAthlete(record: ContentRecord) {
  const color = text(record.data, "color");
  return {
    id: record.id,
    initials: text(record.data, "initials") || record.title.split(/\s+/).map((word) => word[0]).join("").slice(0, 3),
    name: record.title,
    category: text(record.data, "category"),
    focus: text(record.data, "focus"),
    color: (color === "red" || color === "navy" ? color : "blue") as "blue" | "red" | "navy",
    highlights: list(record.data, "highlights"),
    achievementSummary: list(record.data, "achievementSummary"),
    resultsUrl: externalResultsUrl(text(record.data, "resultsUrl")),
    image: text(record.data, "image"),
    alt: text(record.data, "alt") || record.title,
  };
}

export function toMedia(record: ContentRecord) {
  return {
    id: record.id,
    mediaType: text(record.data, "mediaType") === "video" ? "video" as const : "image" as const,
    title: record.title,
    label: text(record.data, "label"),
    image: text(record.data, "image"),
    videoUrl: text(record.data, "videoUrl"),
    alt: text(record.data, "alt") || record.title,
    caption: text(record.data, "caption"),
    credit: text(record.data, "credit"),
    source: text(record.data, "source"),
    license: text(record.data, "license"),
    licenseUrl: text(record.data, "licenseUrl"),
  };
}

export function toPartner(record: ContentRecord) {
  return {
    id: record.id,
    name: record.title,
    mark: text(record.data, "mark"),
    role: text(record.data, "role"),
    website: text(record.data, "website"),
    image: text(record.data, "image"),
    alt: text(record.data, "alt") || `${record.title} logója`,
  };
}

export function toGrant(record: ContentRecord) {
  return {
    id: record.id,
    title: record.title,
    badge: text(record.data, "badge"),
    reference: text(record.data, "reference"),
    goal: text(record.data, "goal"),
    areas: list(record.data, "areas"),
    website: text(record.data, "website"),
  };
}

export function youtubeEmbedUrl(value: string): string | null {
  try {
    const url = new URL(value);
    const host = url.hostname.replace(/^www\./, "");
    let id = "";
    if (host === "youtu.be") id = url.pathname.slice(1).split("/")[0];
    if (host === "youtube.com" || host === "m.youtube.com") {
      id = url.searchParams.get("v") ?? "";
      if (!id && (url.pathname.startsWith("/shorts/") || url.pathname.startsWith("/embed/"))) id = url.pathname.split("/")[2] ?? "";
    }
    return /^[A-Za-z0-9_-]{6,20}$/.test(id) ? `https://www.youtube-nocookie.com/embed/${id}` : null;
  } catch {
    return null;
  }
}
